import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { LoadingButton } from "./LoadingSkeleton";

// Load a saved week back into the plan. Modelled on CopyDayModal - same
// merge/replace choice, same warning treatment.
const LoadWeekModal = ({
  show,
  onHide,
  onConfirm,
  onDelete,
  savedWeeks = [],
  selectedWeekDetail = null,
  onSelectWeek,
  isLoading = false,
  isLoadingDetail = false,
  currentPlanType = "current",
}) => {
  const [selectedId, setSelectedId] = useState(null);
  const [replaceBehavior, setReplaceBehavior] = useState("merge");
  const [updateShoppingList, setUpdateShoppingList] = useState(true);

  useEffect(() => {
    if (show) {
      setSelectedId(null);
      setReplaceBehavior("merge");
      setUpdateShoppingList(true);
    }
  }, [show]);

  const handleSelect = (week) => {
    if (isLoading) return;
    setSelectedId(week.id);
    if (onSelectWeek) onSelectWeek(week.id);
  };

  const missingItems = (selectedWeekDetail?.items || []).filter((i) => i.missing);
  const availableCount =
    (selectedWeekDetail?.items || []).length - missingItems.length;

  const isCurrentWeek = currentPlanType === "current";
  const targetLabel = isCurrentWeek ? "this week" : "next week";
  const canLoad = Boolean(selectedId) && !isLoading && !isLoadingDetail;

  const handleConfirm = () => {
    if (!canLoad) return;
    onConfirm({
      savedWeekId: selectedId,
      mode: replaceBehavior,
      updateShoppingList: isCurrentWeek && updateShoppingList,
    });
  };

  return (
    <Modal
      show={show}
      onHide={isLoading ? undefined : onHide}
      centered
      size="lg"
      backdrop="static"
      keyboard={!isLoading}
    >
      <Modal.Header closeButton={!isLoading}>
        <Modal.Title>
          <i className="fas fa-folder-open me-2"></i>
          Load a Saved Week
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {savedWeeks.length === 0 ? (
          <div className="alert alert-info mb-0">
            <i className="fas fa-info-circle me-2"></i>
            You haven't saved any weeks yet. Build a plan you like, then choose
            "Save This Week".
          </div>
        ) : (
          <>
            <Form.Label>Choose a saved week:</Form.Label>
            <div className="saved-week-list mb-3">
              {savedWeeks.map((week) => (
                <div
                  key={week.id}
                  className={`saved-week-item ${
                    selectedId === week.id ? "selected" : ""
                  }`}
                >
                  {/* The row and the delete control are siblings so neither
                      nests an interactive element inside another. */}
                  <button
                    type="button"
                    className="saved-week-select"
                    onClick={() => handleSelect(week)}
                    disabled={isLoading}
                    aria-pressed={selectedId === week.id}
                  >
                    <span className="saved-week-main">
                      <span className="saved-week-name">{week.name}</span>
                      <span className="saved-week-count">
                        {week.recipe_count} recipes
                      </span>
                    </span>
                    {week.notes && (
                      <span className="saved-week-notes">{week.notes}</span>
                    )}
                    <span className="saved-week-meta">
                      Updated {new Date(week.updated_at).toLocaleDateString()}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="saved-week-delete"
                    onClick={() => onDelete(week)}
                    disabled={isLoading}
                    aria-label={`Delete saved week ${week.name}`}
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </div>
              ))}
            </div>

            {selectedId && missingItems.length > 0 && (
              <div className="alert alert-warning">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <strong>{missingItems.length}</strong> recipe
                {missingItems.length === 1 ? " has" : "s have"} since been deleted and
                will be skipped. {availableCount} will be loaded.
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>If {targetLabel} already has recipes:</Form.Label>
              <Form.Check
                type="radio"
                id="load-week-merge"
                label="Add to existing recipes (merge)"
                checked={replaceBehavior === "merge"}
                onChange={() => setReplaceBehavior("merge")}
                disabled={isLoading}
              />
              <Form.Check
                type="radio"
                id="load-week-replace"
                label="Replace all existing recipes"
                checked={replaceBehavior === "replace"}
                onChange={() => setReplaceBehavior("replace")}
                disabled={isLoading}
              />
            </Form.Group>

            {isCurrentWeek ? (
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  id="load-week-shopping"
                  label="Add ingredients to my shopping list"
                  checked={updateShoppingList}
                  onChange={(e) => setUpdateShoppingList(e.target.checked)}
                  disabled={isLoading}
                />
                <Form.Text className="text-muted">
                  Ingredients are added exactly as if you'd added each recipe by hand.
                </Form.Text>
              </Form.Group>
            ) : (
              <div className="planning-info">
                <i className="fas fa-info-circle"></i>
                <span>
                  Planning Mode: loading into next week won't affect your shopping
                  list until you activate it.
                </span>
              </div>
            )}

            {replaceBehavior === "replace" && (
              <div className="alert alert-warning mt-3 mb-0">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <strong>Warning:</strong> This will delete every recipe currently in{" "}
                {targetLabel}
                {isCurrentWeek
                  ? ", along with the shopping list items they added. Items you added to the shopping list manually are kept."
                  : "."}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isLoading}>
          Cancel
        </Button>
        <LoadingButton
          variant="primary"
          onClick={handleConfirm}
          disabled={!canLoad}
          isLoading={isLoading}
          loadingText="Loading..."
        >
          <i className="fas fa-folder-open me-1"></i>
          Load Week
        </LoadingButton>
      </Modal.Footer>
    </Modal>
  );
};

export default LoadWeekModal;
